/**
 * User Type Definitions
 * Represents different user roles in the HALMAS system
 */

import type { OrganizationType } from './organization';

/**
 * User roles in the HALMAS system
 */
export enum UserRole {
  // Company roles
  COMPANY_ADMIN = 'COMPANY_ADMIN',         // Company administrator
  COMPANY_STAFF = 'COMPANY_STAFF',         // Company staff member

  // Consultancy roles
  CONSULTANT = 'CONSULTANT',               // Halal consultant
  CONSULTANT_ADMIN = 'CONSULTANT_ADMIN',   // Consultancy administrator

  // Certification body roles
  AUDITOR = 'AUDITOR',                     // Halal auditor
  LEAD_AUDITOR = 'LEAD_AUDITOR',          // Lead auditor
  CB_ADMIN = 'CB_ADMIN',                  // Certification body administrator

  // System roles
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',          // System administrator
}

/**
 * User account status
 */
export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  SUSPENDED = 'SUSPENDED',
  LOCKED = 'LOCKED',
}

/**
 * User permissions (for fine-grained access control)
 */
export enum UserPermission {
  // Document permissions
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  VIEW_DOCUMENTS = 'VIEW_DOCUMENTS',
  DELETE_DOCUMENTS = 'DELETE_DOCUMENTS',

  // Application permissions
  CREATE_APPLICATION = 'CREATE_APPLICATION',
  SUBMIT_APPLICATION = 'SUBMIT_APPLICATION',
  REVIEW_APPLICATION = 'REVIEW_APPLICATION',
  APPROVE_APPLICATION = 'APPROVE_APPLICATION',

  // Audit permissions
  SCHEDULE_AUDIT = 'SCHEDULE_AUDIT',
  CONDUCT_AUDIT = 'CONDUCT_AUDIT',
  FINALIZE_AUDIT = 'FINALIZE_AUDIT',

  // Certificate permissions
  ISSUE_CERTIFICATE = 'ISSUE_CERTIFICATE',
  REVOKE_CERTIFICATE = 'REVOKE_CERTIFICATE',
  VIEW_CERTIFICATE = 'VIEW_CERTIFICATE',

  // Payment permissions
  PROCESS_PAYMENT = 'PROCESS_PAYMENT',
  VIEW_PAYMENT = 'VIEW_PAYMENT',
  REFUND_PAYMENT = 'REFUND_PAYMENT',

  // User management
  MANAGE_USERS = 'MANAGE_USERS',
  MANAGE_ROLES = 'MANAGE_ROLES',

  // System
  MANAGE_SYSTEM = 'MANAGE_SYSTEM',
}

/**
 * Base user interface
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string; // Computed: firstName + lastName

  // Role and organization
  role: UserRole;
  organizationId: string;
  organizationType: OrganizationType;

  // Contact information
  phone?: string;
  mobile?: string;
  position?: string;

  // Account status
  status: UserStatus;
  emailVerified: boolean;
  phoneVerified: boolean;

  // Permissions
  permissions: UserPermission[];

  // Profile
  profilePicture?: string;
  language: string; // e.g., "en-US", "ms-MY"
  timezone?: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  lastLoginIp?: string;
}

/**
 * Company user (extends User)
 */
export interface CompanyUser extends User {
  role: UserRole.COMPANY_ADMIN | UserRole.COMPANY_STAFF;

  // Company-specific
  departmentName?: string;
  isMainContact: boolean;
}

/**
 * Consultant user (extends User)
 */
export interface ConsultantUser extends User {
  role: UserRole.CONSULTANT | UserRole.CONSULTANT_ADMIN;

  // Consultant credentials
  certificationNumber?: string;
  certificationExpiryDate?: Date;
  specializations: string[]; // e.g., ["Food Manufacturing", "Logistics"]

  // Statistics
  activeClients: number;
  completedAudits: number;
}

/**
 * Auditor user (extends User)
 */
export interface AuditorUser extends User {
  role: UserRole.AUDITOR | UserRole.LEAD_AUDITOR;

  // Auditor credentials
  auditorLicenseNumber: string;
  licenseExpiryDate: Date;
  accreditationBody: string;

  // Qualifications
  qualifications: string[];
  certifications: string[];

  // Statistics
  auditsCompleted: number;
  certificatesIssued: number;
}

/**
 * Role-based permission mappings
 */
export const ROLE_PERMISSIONS: Record<UserRole, UserPermission[]> = {
  [UserRole.COMPANY_ADMIN]: [
    UserPermission.UPLOAD_DOCUMENTS,
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.DELETE_DOCUMENTS,
    UserPermission.CREATE_APPLICATION,
    UserPermission.SUBMIT_APPLICATION,
    UserPermission.VIEW_CERTIFICATE,
    UserPermission.VIEW_PAYMENT,
    UserPermission.MANAGE_USERS,
  ],
  [UserRole.COMPANY_STAFF]: [
    UserPermission.UPLOAD_DOCUMENTS,
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.VIEW_CERTIFICATE,
    UserPermission.VIEW_PAYMENT,
  ],
  [UserRole.CONSULTANT]: [
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.REVIEW_APPLICATION,
    UserPermission.SCHEDULE_AUDIT,
    UserPermission.CONDUCT_AUDIT,
    UserPermission.VIEW_PAYMENT,
  ],
  [UserRole.CONSULTANT_ADMIN]: [
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.REVIEW_APPLICATION,
    UserPermission.SCHEDULE_AUDIT,
    UserPermission.CONDUCT_AUDIT,
    UserPermission.VIEW_PAYMENT,
    UserPermission.MANAGE_USERS,
  ],
  [UserRole.AUDITOR]: [
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.REVIEW_APPLICATION,
    UserPermission.CONDUCT_AUDIT,
    UserPermission.FINALIZE_AUDIT,
    UserPermission.VIEW_CERTIFICATE,
    UserPermission.VIEW_PAYMENT,
  ],
  [UserRole.LEAD_AUDITOR]: [
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.REVIEW_APPLICATION,
    UserPermission.APPROVE_APPLICATION,
    UserPermission.CONDUCT_AUDIT,
    UserPermission.FINALIZE_AUDIT,
    UserPermission.ISSUE_CERTIFICATE,
    UserPermission.REVOKE_CERTIFICATE,
    UserPermission.VIEW_CERTIFICATE,
    UserPermission.VIEW_PAYMENT,
  ],
  [UserRole.CB_ADMIN]: [
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.REVIEW_APPLICATION,
    UserPermission.APPROVE_APPLICATION,
    UserPermission.SCHEDULE_AUDIT,
    UserPermission.CONDUCT_AUDIT,
    UserPermission.FINALIZE_AUDIT,
    UserPermission.ISSUE_CERTIFICATE,
    UserPermission.REVOKE_CERTIFICATE,
    UserPermission.VIEW_CERTIFICATE,
    UserPermission.PROCESS_PAYMENT,
    UserPermission.VIEW_PAYMENT,
    UserPermission.REFUND_PAYMENT,
    UserPermission.MANAGE_USERS,
    UserPermission.MANAGE_ROLES,
  ],
  [UserRole.SYSTEM_ADMIN]: [
    UserPermission.UPLOAD_DOCUMENTS,
    UserPermission.VIEW_DOCUMENTS,
    UserPermission.DELETE_DOCUMENTS,
    UserPermission.CREATE_APPLICATION,
    UserPermission.SUBMIT_APPLICATION,
    UserPermission.REVIEW_APPLICATION,
    UserPermission.APPROVE_APPLICATION,
    UserPermission.SCHEDULE_AUDIT,
    UserPermission.CONDUCT_AUDIT,
    UserPermission.FINALIZE_AUDIT,
    UserPermission.ISSUE_CERTIFICATE,
    UserPermission.REVOKE_CERTIFICATE,
    UserPermission.VIEW_CERTIFICATE,
    UserPermission.PROCESS_PAYMENT,
    UserPermission.VIEW_PAYMENT,
    UserPermission.REFUND_PAYMENT,
    UserPermission.MANAGE_USERS,
    UserPermission.MANAGE_ROLES,
    UserPermission.MANAGE_SYSTEM,
  ],
};

/**
 * Type guard to check if user is a Company user
 */
export function isCompanyUser(user: User): user is CompanyUser {
  return user.role === UserRole.COMPANY_ADMIN || user.role === UserRole.COMPANY_STAFF;
}

/**
 * Type guard to check if user is a Consultant
 */
export function isConsultantUser(user: User): user is ConsultantUser {
  return user.role === UserRole.CONSULTANT || user.role === UserRole.CONSULTANT_ADMIN;
}

/**
 * Type guard to check if user is an Auditor
 */
export function isAuditorUser(user: User): user is AuditorUser {
  return user.role === UserRole.AUDITOR || user.role === UserRole.LEAD_AUDITOR;
}

/**
 * Helper to check if user has a specific permission
 */
export function hasPermission(user: User, permission: UserPermission): boolean {
  return user.permissions.includes(permission);
}

/**
 * Helper to get all permissions for a role
 */
export function getRolePermissions(role: UserRole): UserPermission[] {
  return ROLE_PERMISSIONS[role] || [];
}
